import { Pipette, Plus } from "lucide-react";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { KIND_DEFAULTS, type PlanKind } from "@mcp/plan";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import "./pickers.css";

/** the 8 category tints of theme.css (--tint-*): identity colours for plan objects */
const TINTS: [string, string][] = [
  ["Amber", "#e8b04a"], ["Orange", "#f0884c"], ["Rose", "#f06484"], ["Violet", "#a888f6"],
  ["Indigo", "#828ef8"], ["Sky", "#60b2e8"], ["Teal", "#40c8ac"], ["Lime", "#b2d654"],
];
const TINT_HEX = TINTS.map(([, c]) => c);
const HEX_RE = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i;

function normHex(v: string): string | null {
  const m = HEX_RE.exec(v.trim());
  if (!m) return null;
  let h = m[1].toLowerCase();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  return `#${h}`;
}

const KIND_PLURAL: Record<PlanKind, string> = { building: "buildings", area: "areas", path: "paths", marker: "markers" };

/**
 * The 8 category tints first, then "custom": a popover with this world's shared colours, any colour (system picker)
 * and a hex field. `onChange` fires live; custom colours are remembered in the world's palette when committed.
 */
export function ColorPicker({ value, onChange, kind }: { value: string; onChange: (c: string) => void; kind?: PlanKind }) {
  const palette = useApp((s) => s.palette);
  const kindColors = useApp((s) => s.kindColors);
  const picker = useRef<HTMLInputElement>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [hex, setHex] = useState(value);
  useEffect(() => setHex(value), [value]);

  // the native "change" event fires once when the system picker closes → remember the colour then
  useEffect(() => {
    const el = picker.current;
    if (!el) return;
    const done = () => controller.store?.rememberColor(el.value);
    el.addEventListener("change", done);
    return () => el.removeEventListener("change", done);
  }, []);

  useEffect(() => {
    if (!open) return;
    const off = (e: PointerEvent) => { if (!wrap.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener("pointerdown", off);
    return () => window.removeEventListener("pointerdown", off);
  }, [open]);

  const pick = (c: string, remember = false) => {
    onChange(c);
    if (remember) controller.store?.rememberColor(c);
  };
  const v = value.toLowerCase();
  const custom = palette.filter((c) => !TINT_HEX.includes(c.toLowerCase()));
  const isTint = TINT_HEX.includes(v);
  const isDefault = kind && (kindColors[kind] ?? KIND_DEFAULTS[kind].color) === value;
  const sw = (c: string, label: string, extra = "") => (
    <button
      key={c}
      type="button"
      role="radio"
      aria-checked={v === c.toLowerCase()}
      className={`sw${v === c.toLowerCase() ? " on" : ""}${extra}`}
      style={{ "--c": c } as CSSProperties}
      title={label}
      aria-label={label}
      onClick={() => pick(c)}
    />
  );

  return (
    <div className="color-picker" ref={wrap} onKeyDown={(e) => { if (e.key === "Escape" && open) { e.stopPropagation(); setOpen(false); } }}>
      <div className="swatches cp-tints" role="radiogroup" aria-label="Colour">
        {TINTS.map(([name, c]) => sw(c, name))}
        <button
          type="button"
          className={`sw sw-custom${!isTint ? " on" : ""}`}
          style={!isTint ? ({ "--c": value } as CSSProperties) : undefined}
          aria-haspopup="dialog"
          aria-expanded={open}
          title={isTint ? "Custom colour" : `Custom colour · ${value}`}
          aria-label="Custom colour"
          onClick={() => setOpen(!open)}
        >
          {isTint && <Plus size={12} aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <div className="cp-pop" role="dialog" aria-label="Custom colour">
          {custom.length > 0 && (
            <>
              <div className="overline">World colours</div>
              <div className="swatches cp-world" role="radiogroup" aria-label="World colours">{custom.map((c) => sw(c, c))}</div>
            </>
          )}
          <div className="overline">Any colour</div>
          <div className="cp-row">
            <button type="button" className="cp-native" style={{ "--c": normHex(value) ?? "#ffffff" } as CSSProperties} onClick={() => picker.current?.click()} title="Pick any colour" aria-label="Pick any colour"><Pipette size={14} aria-hidden="true" /></button>
            <div className="field-box cp-hex">
              <span className="cp-hash" aria-hidden="true">#</span>
              <input
                className="hex"
                value={hex.replace(/^#/, "")}
                spellCheck={false}
                maxLength={7}
                onChange={(e) => { setHex(e.target.value); const n = normHex(e.target.value); if (n) onChange(n); }}
                onBlur={() => { const n = normHex(hex); if (n) pick(n, true); else setHex(value); }}
                onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                aria-label="Hex colour"
              />
            </div>
          </div>
          {kind && !isDefault && (
            <button type="button" className="cp-default" onClick={() => controller.store?.setDefaultColor(kind, value)}>
              Use for new {KIND_PLURAL[kind]}
            </button>
          )}
        </div>
      )}
      <input ref={picker} type="color" className="hidden-color" tabIndex={-1} aria-hidden="true" value={normHex(value) ?? "#ffffff"} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
